import React, { useEffect, useState, useMemo, useCallback } from 'react';
import Header from "../layout/header";
import { _get } from "../libraries/fetch"
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { getAllBranchesURL } from '../libraries/apiUrl';
import {
  fetchBranchLicenseDetail,
  getLicenseExpiryDateString,
  hasBranchLicenseLookup,
  hasBranchLicenseResult,
  isBranchActiveForClient,
  isBranchExpiredForClient,
  isBranchNearToExpireForClient,
  isInactiveOrExpiredBranchStatus,
  isLicenseExpiredForClient,
  isLicenseNearToExpireForClient,
  normalizeRecordId,
  toClientStatusFilterParam
} from '../libraries/licenseExpiry';
import { canAccessNavRoute, canAccessBranchDetails, canViewClientsFilter, getDefaultAccessibleRoute, getBranchGymPackage, isMobileAppBranch, MOBILE_CLIENTS_PATH, resolveClientStatusFilter } from '../libraries/privileges';
import { ensureProductionApiToken } from '../libraries/auth';

// Ignore list as a Set for O(1) lookup
const IGNORE_LIST = new Set([
  "64bf9fe56e1eb79068263fa6",
  "64e5e8f81ca9289ef69a8f3e",
  "64e72bc41ca9289ef6a2ee23",
  "64e84af91ca9289ef6aa8141",
  "64f1c5fc4a74d6538da18e86",
  "651250a01a6bd490a80bb134",
  "665adefe634ebea0973fdb28",
  "6475b8f9ca1bb21ff2fa6913",
  "65d4408ad4d1db5f96081755",
  "65dc317ca2795b43341426f7",
  "6690c8339f9b8cae831b242a",
  "6902f3f64cf88446d8589c25",
  "690300394cf88446d858ef08",
  "690301be4cf88446d858f7f7",
  "6903037c4cf88446d8590436",
  "690304b94cf88446d8590c57",
  "683e8b9a4cf88446d8088b45",
  "67b2faae17701c2c24df4dae",
  "683e78654cf88446d8082575",
  "67879f84165a406d9a846583",
  "66ba093c535ed317419a63a2",
  "68735b604cf88446d8bf2871",
  "68de1d204cf88446d87c5a26",
  "675aae0d12e3fdda6677d2df",
  "6642eab31678eb98b616af02",
  "678f384699156d3a1d3aa6b7",
  "685e43704cf88446d872c023",
]);

// Helper to check if license is expired for card display
const isLicenseExpired = (licenseData) => isLicenseExpiredForClient(licenseData);

const isLicenseNearToExpire = (licenseData) => isLicenseNearToExpireForClient(licenseData);

const isInactiveOrExpiredStatus = (status) => isInactiveOrExpiredBranchStatus(status);

const formatDate = (dateValue) => {
  if (!dateValue) return "N/A";
  const date = new Date(dateValue);
  if (isNaN(date.getTime())) return "N/A";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
};

// Format license display
const formatLicenseInfo = (licenseData) => {
  const expiryDate = getLicenseExpiryDateString(licenseData);

  if (!expiryDate) {
    return {
      text: "License has Expired.",
      isExpired: true,
      dueDate: "N/A",
      expiredOn: "N/A"
    };
  }

  const futureDate = new Date(expiryDate).getTime();
  const formattedDueDate = formatDate(expiryDate);

  if (isNaN(futureDate)) {
    return {
      text: "License has Expired.",
      isExpired: true,
      dueDate: "N/A",
      expiredOn: "N/A"
    };
  }

  const days = Math.floor((futureDate - Date.now()) / (1000 * 60 * 60 * 24));
  if (days < 0) {
    return {
      text: "License has Expired.",
      isExpired: true,
      dueDate: formattedDueDate,
      expiredOn: formattedDueDate
    };
  }

  return {
    text: `License Will Expire In : (Days ${days})`,
    isExpired: false,
    days,
    dueDate: formattedDueDate,
    expiredOn: "-"
  };
};

function Branches() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobileAppClients = location.pathname === MOBILE_CLIENTS_PATH;
  const canAccessClients = canAccessNavRoute(isMobileAppClients ? MOBILE_CLIENTS_PATH : "/branches");
  const canOpenBranchDetails = canAccessBranchDetails();
  const [searchParams, setSearchParams] = useSearchParams();
  const [branchSourceList, setBranchSourceList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [licenseDataMap, setLicenseDataMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [packageFilter, setPackageFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState(() => (
    resolveClientStatusFilter(searchParams.get('filter'))
  ));

  const changeStatusFilter = useCallback((filter) => {
    setStatusFilter(filter);
    setSearchParams({ filter: toClientStatusFilterParam(filter) }, { replace: true });
  }, [setSearchParams]);

  const gotoBranchDetails = useCallback((id, currentIndex, branchIds) => {
    navigate("/branches/" + id, {
      state: {
        branchId: id,
        currentIndex,
        branchIds,
        clientsFilter: statusFilter,
        clientsSource: isMobileAppClients ? "mobile" : "web"
      }
    });
  }, [navigate, statusFilter, isMobileAppClients]);

  useEffect(() => {
    if (!canAccessClients) {
      navigate(getDefaultAccessibleRoute(), { replace: true });
    }
  }, [canAccessClients, navigate]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Keep URL filter in sync with role-based client filter privileges
  useEffect(() => {
    setStatusFilter(resolveClientStatusFilter(searchParams.get('filter')));
  }, [searchParams]);

  // Fetch branches and license data
  useEffect(() => {
    setLoading(true);
    _get(getAllBranchesURL())
      .then((data) => {
        // Handle different response formats
        let branches = [];
        if (Array.isArray(data)) {
          branches = data;
        } else if (data && data.response && Array.isArray(data.response)) {
          branches = data.response;
        } else if (data && data.data && Array.isArray(data.data)) {
          branches = data.data;
        }

        // Filter ignored branches and normalize ids for consistent cache/API lookups.
        // Mobile App Clients: source is "Mobile App" (or TecGym Mobile). Web App Clients: everyone else.
        const list = branches
          .map((branch) => ({
            ...branch,
            _id: normalizeRecordId(branch._id || branch.id),
            company: normalizeRecordId(branch.company)
          }))
          .filter((branch) => branch._id && !IGNORE_LIST.has(branch._id))
          .filter((branch) => (
            isMobileAppClients
              ? isMobileAppBranch(branch)
              : !isMobileAppBranch(branch)
          ));
        setBranchSourceList(list);
        setLoading(false);

        // Fetch license data in background (non-blocking)
        const branchesToFetch = list.filter(branch => branch.company && branch._id);
        const licenseMap = {};

        // Process in batches
        const batchSize = 20;
        const fetchBatches = async () => {
          await ensureProductionApiToken();

          for (let i = 0; i < branchesToFetch.length; i += batchSize) {
            const batch = branchesToFetch.slice(i, i + batchSize);

            const batchResults = await Promise.all(
              batch.map(async (branch) => {
                const branchId = normalizeRecordId(branch._id);
                const data = await fetchBranchLicenseDetail(branch);
                return { branchId, data: data || null };
              })
            );

            batchResults.forEach(({ branchId, data }) => {
              licenseMap[branchId] = data ?? null;
            });

            // Update state after each batch for progressive loading
            setLicenseDataMap(prev => ({ ...prev, ...licenseMap }));
          }
        };

        fetchBatches();
      })
      .catch(err => {
        console.error('Error fetching branches:', err);
        setLoading(false);
        setBranchSourceList([]);
      });
  }, [isMobileAppClients]);


  // Compute expired branches from license data (memoized)
  const expiredBranchIds = useMemo(() => {
    const expired = new Set();
    branchSourceList.forEach((branch) => {
      const branchId = normalizeRecordId(branch._id);
      if (isBranchExpiredForClient(branch, licenseDataMap)) {
        expired.add(branchId);
      }
    });
    return expired;
  }, [branchSourceList, licenseDataMap]);

  const isBranchExpired = useCallback((branch) => (
    isBranchExpiredForClient(branch, licenseDataMap)
  ), [licenseDataMap]);

  const isBranchActive = useCallback((branch) => (
    isBranchActiveForClient(branch, licenseDataMap)
  ), [licenseDataMap]);

  const isBranchNearToExpire = useCallback((branch) => (
    isBranchNearToExpireForClient(branch, licenseDataMap)
  ), [licenseDataMap]);

  // Calculate counts for active and inactive branches (memoized)
  const statusCounts = useMemo(() => {
    if (!branchSourceList.length) return { all: 0, active: 0, Expired: 0, NearToExpire: 0 };

    let allCount = 0;
    let activeCount = 0;
    let expiredCount = 0;
    let nearToExpireCount = 0;

    branchSourceList.forEach(branch => {
      allCount++;

      if (isBranchNearToExpire(branch)) {
        nearToExpireCount++;
      }

      if (isBranchActive(branch)) {
        activeCount++;
      } else if (isBranchExpired(branch)) {
        expiredCount++;
      }
    });

    return { all: allCount, active: activeCount, Expired: expiredCount, NearToExpire: nearToExpireCount };
  }, [branchSourceList, isBranchActive, isBranchExpired, isBranchNearToExpire]);

  // Memoized filtered branch list
  const branchList = useMemo(() => {
    if (!branchSourceList.length) return [];

    const query = debouncedSearchQuery.toLowerCase().trim();

    const filtered = branchSourceList.filter(branch => {
      if (statusFilter === 'all') {
        // All clients are shown; package and search filters still apply below.
      } else if (statusFilter === 'active') {
        if (!isBranchActive(branch)) {
          return false;
        }
      } else if (statusFilter === 'NearToExpire') {
        if (!isBranchNearToExpire(branch)) {
          return false;
        }
      } else if (statusFilter === 'Expired') {
        if (!isBranchExpired(branch)) {
          return false;
        }
      }

      // Package filter
      if (packageFilter !== 'all') {
        const branchPkg = getBranchGymPackage(branch).toLowerCase();
        if (branchPkg !== packageFilter.toLowerCase()) return false;
      }

      // Search filter
      if (query && (!branch.name || !branch.name.toLowerCase().includes(query))) {
        return false;
      }

      return true;
    });

    return filtered;
  }, [debouncedSearchQuery, branchSourceList, statusFilter, packageFilter, isBranchActive, isBranchExpired, isBranchNearToExpire]);

  const renderBranchCards = () => {
    if (loading) {
      return <p style={{ padding: '20px', color: '#888' }}>Loading branches...</p>;
    }

    if (branchList.length === 0) {
      return (
        <p style={{ padding: '20px', color: '#888' }}>
          {isMobileAppClients ? "No Mobile App clients found" : "No branches found"}
        </p>
      );
    }

    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))',
        gap: '20px',
        padding: '20px'
      }}>
        {branchList.map((branch, index) => {
          const branchExpired = isBranchExpired(branch);
          const branchId = normalizeRecordId(branch._id);
          const showExpiredAnimation = branchExpired;
          const licenseData = licenseDataMap[branchId];

          // Check branch status first - if status is expired/inactive, show expired immediately
          const status = branch.status;
          const branchStatus = status ? String(status).toLowerCase().trim() : '';
          const branchHasInactiveOrExpiredStatus = isInactiveOrExpiredStatus(branchStatus);
          const licenseCheckCompleted = !hasBranchLicenseLookup(branch) ||
            hasBranchLicenseResult(branchId, licenseDataMap);

          // Determine license info - check license data first if available
          let licenseInfo;
          if (licenseCheckCompleted) {
            if (licenseData) {
              licenseInfo = formatLicenseInfo(licenseData);
            } else if (hasBranchLicenseLookup(branch) || branchHasInactiveOrExpiredStatus) {
              // Missing license detail (or expired status) => Expired, not Active.
              licenseInfo = {
                text: "License has Expired.",
                isExpired: true,
                dueDate: "N/A",
                expiredOn: "N/A"
              };
            } else {
              licenseInfo = {
                text: "License unavailable",
                isExpired: false,
                dueDate: "N/A",
                expiredOn: "N/A"
              };
            }
          } else if (branchHasInactiveOrExpiredStatus) {
            // Status indicates expired - show expired immediately
            licenseInfo = { text: "License has Expired.", isExpired: true, dueDate: "N/A", expiredOn: "N/A" };
          } else {
            // Still waiting for license data
            licenseInfo = { text: "Loading...", isExpired: false, dueDate: "N/A", expiredOn: "-" };
          }

          // Check if days are 3 or less
          const isDaysLimitCritical = licenseInfo.days !== undefined && licenseInfo.days >= 0 && licenseInfo.days <= 3;
          const showNearToExpireAnimation = statusFilter === 'NearToExpire' || isDaysLimitCritical;

          return (
            <div
              key={branch._id}
              className={showExpiredAnimation ? 'pulse-hover-expired' : showNearToExpireAnimation ? 'blink-warning' : 'branch-card'}
              style={{
                backgroundColor: '#2a2d35',
                border: showNearToExpireAnimation ? '2px solid #ff8c00' : '1px solid #444',
                borderRadius: '12px',
                padding: '20px',
                transition: showExpiredAnimation || showNearToExpireAnimation ? 'none' : 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                boxShadow: showNearToExpireAnimation ? '0 0 15px rgba(255, 140, 0, 0.6)' : '0 2px 8px rgba(0,0,0,0.3)',
                overflow: 'hidden'
              }}
            >
              <div style={{ marginBottom: '15px' }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#fff',
                  margin: '0 0 10px 0',
                  borderBottom: '2px solid #187718',
                  paddingBottom: '8px'
                }}>
                  {branch.name || "N/A"}
                </h3>
              </div>

              <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '15px', color: '#888' }}>Phone</span>
                <span style={{ fontSize: '16px', color: '#fff', fontWeight: '600' }}>{branch.phone || "N/A"}</span>
              </div>

              <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '15px', color: '#888' }}>Status</span>
                <span style={{
                  fontSize: '16px',
                  color: (branchExpired || licenseInfo.isExpired) ? '#d51717' : '#4ade80',
                  fontWeight: '600'
                }}>
                  {(branchExpired || licenseInfo.isExpired) ? 'Expired' : (branch.status || 'N/A')}
                </span>
              </div>

              <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '15px', color: '#888' }}>Valid Till</span>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: licenseInfo.isExpired ? '#d51717' : isDaysLimitCritical ? '#ff8c00' : '#fff'
                }}>
                  {licenseInfo.text}
                </div>
              </div>

              <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '15px', color: '#888' }}>Due Date</span>
                <span style={{ fontSize: '14px', color: '#fff', fontWeight: '600' }}>
                  {licenseInfo.dueDate || 'N/A'}
                </span>
              </div>

              <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '15px', color: '#888' }}>{isMobileAppClients ? "Branch Type" : "Gym Package"}</span>
                <span style={{ fontSize: '16px', color: '#fff', fontWeight: '600' }}>
                  {isMobileAppClients
                    ? (branch.branchType || getBranchGymPackage(branch) || "Not available")
                    : (branch.gymPackage || "Not available")}
                </span>
              </div>

              {canOpenBranchDetails && (
              <button
                onClick={() => gotoBranchDetails(branch._id, index, branchList.map((item) => item._id))}
                className="details-btn"
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#187718',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginTop: '10px',
                  transition: 'all 0.3s ease'
                }}
              >
                Go to Details
              </button>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  if (!canAccessClients) {
    return null;
  }

  return (
    <div className="main">
      <style>{`
        @keyframes pulseHover {
          0%, 100% {
            transform: translateY(0) scale(1);
            background-color: #2a2d35;
            border-color: #444;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          }
          50% {
            transform: translateY(-6px) scale(1.02);
            background-color: #33363f;
            border-color: #ff4444;
            box-shadow: 0 12px 24px rgba(0,0,0,0.4), 0 0 20px rgba(255, 68, 68, 0.4);
          }
        }
        @keyframes blinkWarning {
          0%, 100% {
            border-color: #ff8c00;
            box-shadow: 0 0 15px rgba(255, 140, 0, 0.6), 0 2px 8px rgba(0,0,0,0.3);
          }
          50% {
            border-color: #ffa500;
            box-shadow: 0 0 25px rgba(255, 140, 0, 1), 0 2px 12px rgba(0,0,0,0.5);
          }
        }
        .pulse-hover-expired {
          animation: pulseHover 1s ease-in-out infinite;
        }
        .blink-warning {
          animation: blinkWarning 1.2s ease-in-out infinite;
        }
        .branch-card:hover {
          background-color: #33363f;
          transform: translateY(-6px) scale(1.02);
          box-shadow: 0 12px 24px rgba(0,0,0,0.4), 0 0 20px rgba(24, 119, 24, 0.3);
          border: 1px solid #187718;
        }
        .details-btn:hover {
          background-color: #20a020;
          transform: scale(1.05);
        }
      `}</style>
      <Header />
      <div style={{
        backgroundColor: '#2a2d35',
        border: '1px solid #444',
        borderRadius: '12px',
        padding: 'clamp(16px, 3vw, 25px) clamp(16px, 3vw, 30px)',
        marginBottom: '30px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        boxSizing: 'border-box'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap', marginBottom: '5px' }}>
              <h1 style={{
                margin: 0,
                fontSize: 'clamp(22px, 5vw, 32px)',
                fontWeight: 'bold',
                color: '#fff',
                background: 'linear-gradient(135deg, #fff 0%, #e0e0e0 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                {isMobileAppClients
                  ? (statusFilter === 'all'
                    ? 'Mobile App Clients'
                    : statusFilter === 'active'
                      ? 'Active Mobile App Clients'
                      : statusFilter === 'Expired'
                        ? 'Expired Mobile App Clients'
                        : statusFilter === 'NearToExpire'
                          ? 'Near to Expire Mobile App Clients'
                          : 'Mobile App Clients')
                  : (statusFilter === 'all'
                    ? 'All Branches'
                    : statusFilter === 'active'
                      ? 'Active Branches'
                      : statusFilter === 'Expired'
                        ? 'Expired Branches'
                        : statusFilter === 'NearToExpire'
                          ? 'Near to Expire Branches'
                          : 'Branches')}
              </h1>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: '#187718',
                padding: '6px 14px',
                borderRadius: '20px'
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <span style={{ color: '#fff', fontSize: '14px', fontWeight: '600' }}>{branchList.length} Total</span>
              </div>
            </div>
            <div className="branch-status-filters" style={{ display: 'flex', gap: '12px', marginTop: '20px', flexWrap: 'wrap' }}>
              {canViewClientsFilter('all') && (
              <button
                onClick={() => changeStatusFilter('all')}
                style={{
                  padding: '8px 16px',
                  backgroundColor: statusFilter === 'all' ? '#4caf50' : '#1b1e25',
                  color: '#fff',
                  border: '1px solid #24272f',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: 'larger',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
                onMouseEnter={(e) => {
                  if (statusFilter !== 'all') {
                    e.target.style.backgroundColor = '#2a2d35';
                  }
                }}
                onMouseLeave={(e) => {
                  if (statusFilter !== 'all') {
                    e.target.style.backgroundColor = '#1b1e25';
                  }
                }}
              >
                <span>All</span>
                <span style={{
                  backgroundColor: statusFilter === 'all' ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  {statusCounts.all}
                </span>
              </button>
              )}
              {canViewClientsFilter('active') && (
              <button
                onClick={() => changeStatusFilter('active')}
                style={{
                  padding: '8px 16px',
                  backgroundColor: statusFilter === 'active' ? '#4caf50' : '#1b1e25',
                  color: '#fff',
                  border: '1px solid #24272f',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: 'larger',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
                onMouseEnter={(e) => {
                  if (statusFilter !== 'active') {
                    e.target.style.backgroundColor = '#2a2d35';
                  }
                }}
                onMouseLeave={(e) => {
                  if (statusFilter !== 'active') {
                    e.target.style.backgroundColor = '#1b1e25';
                  }
                }}
              >
                <span>Active</span>
                <span style={{
                  backgroundColor: statusFilter === 'active' ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  {statusCounts.active}
                </span>
              </button>
              )}
              {canViewClientsFilter('NearToExpire') && (
              <button
                onClick={() => changeStatusFilter('NearToExpire')}
                style={{
                  padding: '8px 16px',
                  backgroundColor: statusFilter === 'NearToExpire' ? '#4caf50' : '#1b1e25',
                  color: '#fff',
                  border: '1px solid #24272f',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: 'Larger',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  if (statusFilter !== 'NearToExpire') {
                    e.target.style.backgroundColor = '#2a2d35';
                  }
                }}
                onMouseLeave={(e) => {
                  if (statusFilter !== 'NearToExpire') {
                    e.target.style.backgroundColor = '#1b1e25';
                  }
                }}
              >
                <span>Near to Expire</span>
                <span style={{
                  backgroundColor: statusFilter === 'NearToExpire' ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  {statusCounts.NearToExpire}
                </span>
              </button>
              )}
              {canViewClientsFilter('Expired') && (
              <button
                onClick={() => changeStatusFilter('Expired')}
                style={{
                  padding: '8px 16px',
                  backgroundColor: statusFilter === 'Expired' ? '#4caf50' : '#1b1e25',
                  color: '#fff',
                  border: '1px solid #24272f',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: 'Larger',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  if (statusFilter !== 'Expired') {
                    e.target.style.backgroundColor = '#2a2d35';
                  }
                }}
                onMouseLeave={(e) => {
                  if (statusFilter !== 'Expired') {
                    e.target.style.backgroundColor = '#1b1e25';
                  }
                }}
              >
                <span>Expired</span>
                <span style={{
                  backgroundColor: statusFilter === 'Expired' ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  {statusCounts.Expired}
                </span>
              </button>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
            <div className="fluid-search" style={{ position: 'relative' }}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#888"
                strokeWidth="2"
                style={{
                  position: 'absolute',
                  left: '14px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none'
                }}
              >
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder="Search Clients By Name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '14px 45px 14px 45px',
                  fontSize: '15px',
                  backgroundColor: '#1e2127',
                  border: '1px solid #444',
                  borderRadius: '10px',
                  color: '#fff',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#187718';
                  e.target.style.boxShadow = '0 0 0 3px rgba(24, 119, 24, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#444';
                  e.target.style.boxShadow = 'none';
                }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{
                    position: 'absolute',
                    right: '14px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#888',
                    transition: 'color 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = '#888';
                  }}
                  type="button"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Package Filter Buttons - Below search field */}
          {!isMobileAppClients && (
          <div style={{
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap',
            marginTop: '20px',
            width: '100%'
          }}>
            {['all', 'Standard', 'Premium', 'Enterprise'].map((pkg) => (
              <button
                key={pkg}
                onClick={() => setPackageFilter(pkg)}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  borderRadius: '8px',
                  border: '2px solid',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  textTransform: 'capitalize',
                  backgroundColor: packageFilter === pkg
                    ? '#187718'
                    : 'transparent',
                  borderColor: packageFilter === pkg
                    ? '#187718'
                    : '#444',
                  color: packageFilter === pkg
                    ? '#fff'
                    : '#ccc',
                  boxShadow: packageFilter === pkg
                    ? '0 4px 12px rgba(24, 119, 24, 0.3)'
                    : 'none'
                }}
                onMouseEnter={(e) => {
                  if (packageFilter !== pkg) {
                    e.currentTarget.style.borderColor = '#187718';
                    e.currentTarget.style.color = '#fff';
                    e.currentTarget.style.backgroundColor = 'rgba(24, 119, 24, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (packageFilter !== pkg) {
                    e.currentTarget.style.borderColor = '#444';
                    e.currentTarget.style.color = '#ccc';
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {pkg === 'all' ? 'All' : pkg}
              </button>
            ))}
          </div>
          )}
        </div>
      </div>
      {renderBranchCards()}
    </div>
  );
}

export default Branches;
